import { useAuthUser } from "../../../contexts/AuthUserContext";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

import CallChart from "../../../components/insights/CallChart";
import MessagesChart from "../../../components/insights/MessagesChart";
import SuccessChart from "../../../components/insights/SuccessChart";

import { useSentiment } from "../../../contexts/SentimentContext";

import TicketCard from "../../../components/tickets/ticketCard";
import { useTickets } from "../../../contexts/TicketContext.jsx";
import { useCallData } from "../../../contexts/CallDataContext.jsx";
import { useWeek } from "../../../contexts/WeekContext";
import { useCustomer } from "../../../contexts/CustomerContext";
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { parseCustomTimestamp } from "../../../utils/dateUtils";
import ChatbotWidget from "../../../components/chatbot-embed/ChatbotWidget";
import { fetchAccountDetails } from "../../../api/ticketData";

const KronkHome = () => {
    const user = useAuthUser();
    const { currentCustomer } = useCallData();
    const { getEffectiveCustomerId, isImpersonating } = useCustomer();
    const { sentimentCounts } = useSentiment();
    const navigate = useNavigate();
    const { dateRange } = useWeek();

    // console.log('Sentiments', sentimentCounts);

    const { tickets, isLoading: isLoadingTickets, error: ticketsError } = useTickets();

    // Check if user is helios admin
    const isHeliosAdmin = user?.role === 'helios_admin' || user?.roles?.includes('helios_admin');

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        insights: false,
        sentiment: false,
        tickets: false
    });

    // Chatbot state
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, insights: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, sentiment: true }));
        }, 500));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, tickets: true }));
        }, 700));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Get assistant name based on client
    const getAssistantName = () => {
        const clientId = getEffectiveCustomerId();
        if (clientId === 'celeste' || clientId === 'helios') {
            return 'Celeste';
        } else if (clientId === 'wards') {
            return 'Randi';
        } else if (clientId === 'scale') {
            return 'BDR';
        } else if (clientId === 'kronk') {
            return 'Kronk';
        } else {
            // For other clients, use their ID in Title Case
            return clientId ? clientId.charAt(0).toUpperCase() + clientId.slice(1).toLowerCase() : 'Assistant';
        }
    };

    // Get assistant phone number based on client
    const getAssistantPhoneNumber = () => {
        const clientId = getEffectiveCustomerId();
        if (clientId === 'wards') {
            return '+12489709365';
        }
        return null; // N/A for celeste/helios and all other clients
    };

    // Add this function to filter calls by date range
    const getFilteredCalls = () => {
        if (!currentCustomer || !dateRange?.start || !dateRange?.end) return [];
        
        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);
        
        return currentCustomer.filter(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (!callDate) return false;
            return isWithinInterval(callDate, { start: rangeStart, end: rangeEnd });
        });
    };

    // Use this function where you need filtered call counts
    const filteredCalls = getFilteredCalls();
    const totalFilteredCalls = filteredCalls.length;

    // State for filtered tickets
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [isFilteringTickets, setIsFilteringTickets] = useState(false);

    // Filter tickets when impersonating
    useEffect(() => {
        const filterTickets = async () => {
            if (isHeliosAdmin && isImpersonating && tickets.length > 0) {
                setIsFilteringTickets(true);
                
                const impersonatedCustomerId = getEffectiveCustomerId()?.toLowerCase();
                if (!impersonatedCustomerId) {
                    setFilteredTickets(tickets);
                    setIsFilteringTickets(false);
                    return;
                }

                const filteredTickets = [];
                for (const ticket of tickets) {
                    if (!ticket.accountId) {
                        continue;
                    }

                    try {
                        const accountDetails = await fetchAccountDetails(ticket.accountId);
                        const accountName = accountDetails?.accountName || '';
                        const accountFirstWord = accountName.split(' ')[0].toLowerCase();
                        
                        if (accountFirstWord === impersonatedCustomerId) {
                            filteredTickets.push(ticket);
                        }
                    } catch (error) {
                        console.warn(`Failed to fetch account details for ticket ${ticket.id}:`, error);
                        // Continue with other tickets even if one account fetch fails
                    }
                }

                setFilteredTickets(filteredTickets);
                setIsFilteringTickets(false);
            } else {
                setFilteredTickets(tickets);
            }
        };

        filterTickets();
    }, [tickets, isHeliosAdmin, isImpersonating, getEffectiveCustomerId]);

    return (
        <div className="responsive-page-layout scrollable">
            <div className={`home-title dashboard-header-responsive transition-all duration-700 ease-out ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
            }`}>
                <div className="flex flex-col gap-2 items-start w-full lg:w-auto">
                    <h4 className="text-xl sm:text-2xl flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                        <div className="flex flex-row gap-2 items-center">
                            <Icon icon="mdi:hand-wave" className="text-primary text-2xl sm:text-3xl"></Icon>
                            Hello <span className="text-primary font-bold">{user.name}</span>
                        </div>
                    </h4>
                    <p className="text-font-secondary text-sm sm:text-base lg:text-lg">Here's a quick snapshot of your AI assistant's performance this week.</p>
                </div>
                <div className="header-right-3">
                    <div className='flex flex-col sm:flex-row gap-2 w-full lg:w-fit'>
                        <div className="flex flex-row gap-1 w-fit items-center text-xs sm:text-sm p-2 rounded-md bg-bgSecondary border border-border">
                            <Icon icon="mingcute:ai-fill" className="text-lg sm:text-xl text-secondary"></Icon>
                            <p><strong>AI Assistant:</strong> {getAssistantName()}</p>
                        </div>
                        {getAssistantPhoneNumber() ? (
                            <div className={`
                                flex flex-row gap-1 items-center w-fit text-xs sm:text-sm p-2 rounded-md button-container
                                cursor-pointer hover:bg-neo-button transition-colors`}
                                onClick={() => window.location.href = `tel:${getAssistantPhoneNumber()}`}
                            >
                                <Icon icon="material-symbols:call" className="text-lg sm:text-xl text-secondary"></Icon>
                                <p className="hidden sm:inline"><strong>Assistant Phone Number:</strong> +1 (248) 970 9365</p>
                                <p className="sm:hidden"><strong>Phone:</strong> +1 (248) 970 9365</p>
                            </div>
                        ) : (
                            // Update this when chatbot is added
                            <div className={`
                                hidden flex-row gap-1 items-center w-fit text-xs sm:text-sm p-2 rounded-md button-container
                                cursor-pointer`}
                                onClick={() => setIsChatbotOpen(true)}
                            >
                                <Icon icon="material-symbols:chat" className="text-lg sm:text-xl text-secondary"></Icon>
                                <p className="hidden sm:inline">Chat with Assistant</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Key Insights */}
            <div className={`home-insights w-full flex flex-col gap-2 lg:gap-3 transition-all duration-1000 ease-out ${
                animationStates.insights
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="home-insights-title responsive-flex-left">
                    <div className="subheader-textbox">
                        {/* <Icon icon="system-uicons:graph-bar" className="text-secondary responsive-icon-sm"></Icon> */}
                        Key Insights
                    </div>
                    <Link to={`/app/kronk/insights`} id="key-insights-button" className="flex flex-row items-center gap-1 text-accent text-sm sm:text-base">
                        View all <Icon icon="mingcute:right-line" className="text-lg sm:text-xl"></Icon>
                    </Link>
                </div>
                <div className="dashboard-insights-responsive">
                    <div className="chart-container-responsive">
                        <CallChart clientId={getEffectiveCustomerId()} />
                    </div>
                    <div className="chart-container-responsive">
                        <MessagesChart clientId={getEffectiveCustomerId()} />
                    </div>
                    <div className="chart-container-responsive">
                        <SuccessChart />
                    </div>
                </div>
            </div>
            {/* Call Sentiment Summary */}
            <div className={`home-insights w-full flex flex-col gap-2 lg:gap-3 transition-all duration-1000 ease-out ${
                animationStates.sentiment
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="subheader-textbox">
                    {/* <Icon icon="fluent:person-call-24-regular" className="text-secondary responsive-icon-sm"></Icon> */}
                    Chat Sentiment Summary
                </div>
                <div className="dashboard-sentiment-responsive">
                    <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="icon-park-outline:positive-dynamics" className="text-lg sm:text-xl text-green-500" />
                            <span className="text-sm sm:text-base font-semibold">Positive</span>
                        </div>
                        <span className="text-green-500 font-bold text-lg sm:text-xl">{sentimentCounts.positive || 0}</span>
                    </div>
                    <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="icon-park-outline:dividing-line-one" className="text-lg sm:text-xl text-yellow-400" />
                            <span className="text-sm sm:text-base font-semibold">Neutral</span>
                        </div>
                        <span className="text-yellow-400 font-bold text-lg sm:text-xl">{sentimentCounts.neutral || 0}</span>
                    </div>
                    <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                        <div className="flex items-center gap-1">
                            <Icon icon="icon-park-outline:negative-dynamics" className="text-lg sm:text-xl text-error" />
                            <span className="text-sm sm:text-base font-semibold">Negative</span>
                        </div>
                        <span className="text-error font-bold text-lg sm:text-xl">{sentimentCounts.negative || 0}</span>
                    </div>
                </div>
            </div>
            {/* Open Tickets */}
            <div className={`home-insights w-full flex flex-col gap-4 transition-all duration-1000 ease-out h-full ${
                animationStates.tickets
                    ? 'opacity-100'
                    : 'opacity-0'
            }`}>
                <div className="subheader-textbox">
                    {/* <Icon icon="f7:tickets" className="text-secondary responsive-icon-sm"></Icon> */}
                    Open Tickets
                </div>
                <div className="dashboard-alerts-responsive h-full">
                    {(isLoadingTickets || isFilteringTickets) && <p className="text-gray-400 col-span-full">Loading tickets...</p> /* Loading indicator */}
                    {ticketsError && <p className="text-yellow-500 col-span-full">⚠️ {ticketsError}</p> /* Error message */}
                    {!isLoadingTickets && !isFilteringTickets && !ticketsError && filteredTickets.length === 0 && (
                        <div className="flex flex-col items-center justify-center w-full text-center text-gray-400">
                            <Icon icon="carbon:no-ticket" className="text-6xl mb-4" />
                            <p className="text-lg">No tickets found</p>
                        </div>
                    )}
                    {!isLoadingTickets && !isFilteringTickets && !ticketsError && filteredTickets.length > 0 && (() => {
                        const openTickets = filteredTickets.filter(ticket => ticket.status === 'Open');
                        if (isHeliosAdmin && openTickets.length === 0) {
                            return (
                                <div className="flex flex-col items-center justify-center w-full h-full text-center text-gray-400">
                                    <Icon icon="carbon:no-ticket" className="text-6xl mb-4" />
                                    <p className="text-lg">No open tickets</p>
                                </div>
                            );
                        }
                        return openTickets.slice(0, 4).map(ticket => (
                            <TicketCard ticket={ticket} key={ticket.id || ticket.ticketNumber} />
                        ));
                    })()}
                </div>
            </div>
            
            {/* Chatbot Widget */}
            <ChatbotWidget 
                isOpen={isChatbotOpen} 
                onClose={() => setIsChatbotOpen(false)} 
            />
        </div>
    );
};

export default KronkHome;
